import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Api } from "../../lib/api";
import { MonitorStore } from "../../lib/cache";
import { Button, Card, UsageBar, StatusDot, Badge, EmptyState, Spinner } from "../../components/ui";
import ThresholdEditView from "./ThresholdEditView";
import ResetUsageView from "./ResetUsageView";
import { notify } from "../../lib/notify";
import "./monitor.css";

export default function Monitor() {
  const [snap, setSnap] = useState(MonitorStore.get());
  const [loading, setLoading] = useState(!snap);
  const [editTarget, setEditTarget] = useState(null);
  const [rechargeDialogOpen, setRechargeDialogOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [openMenuId, setOpenMenuId] = useState(location.state?.autoOpenMenuId ?? null);

  useEffect(() => {
    if (location.state?.autoOpenMenuId) {
      // Use React Router to clear state instead of mutating window.history directly
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = useCallback(async () => {
    try {
      const data = await Api.getMonitor();
      setSnap(data);
      MonitorStore.save(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function reactivate(id) {
    await Api.reactivateCutoff(id);
    refresh();
  }

  if (editTarget) {
    if (editTarget.kind === "reset") {
      return (
        <ResetUsageView
          target={editTarget}
          onBack={() => setEditTarget(null)}
          onReset={async () => {
            await Api.resetCredentialUsage(editTarget.credentialId);
            setEditTarget(null);
            refresh();
          }}
        />
      );
    }
    return (
      <ThresholdEditView
        target={editTarget}
        onBack={() => setEditTarget(null)}
        onSave={async (payload) => {
          await Api.setThreshold({ credential_id: editTarget.credentialId, ...payload });
          setEditTarget(null);
          refresh();
        }}
      />
    );
  }

  const tokens = snap?.tokens;
  const tokensAvailable = tokens?.tokens_available ?? 0;
  const daysAvailable = tokens?.days_available ?? 0;
  const totalAllowance = (tokens?.tokens_available ?? 0) + (tokens?.tokens_used ?? 0);
  const outOfTokens = tokensAvailable <= 0 || daysAvailable <= 0;

  const cardsByLlm = {};
  (snap?.llm_cards ?? []).forEach((c) => {
    (cardsByLlm[c.llm] ||= []).push(c);
  });

  return (
    <div>
      <h1 style={{ fontSize: 24 }}>Monitor</h1>
      <p style={{ color: "var(--slate)", fontSize: 13.5, marginTop: 4, marginBottom: 20 }}>
        Track token usage across your connected providers and manage alerts.
      </p>

      {loading && !snap ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "60px 0" }}>
          <Spinner size={28} />
        </div>
      ) : (
        <>
          <PlanUsageCard
            totalTokensUsed={tokens?.tokens_used ?? 0}
            totalAllowance={totalAllowance}
            daysAvailable={daysAvailable}
          />

          <div style={{ height: 24 }} />

          {Object.keys(cardsByLlm).length === 0 ? (
            <EmptyState>
              No providers configured yet. Head to the Providers tab to add a key or generate a
              pass-through URL for your first provider.
            </EmptyState>
          ) : (
            <>
              <h3 style={{ fontSize: 16, marginBottom: 10 }}>Connected Providers</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {Object.entries(cardsByLlm).map(([llm, cards]) => (
                  <ProviderMonitorCard
                    key={llm}
                    llm={llm}
                    cards={cards}
                    totalAllowance={totalAllowance}
                    outOfTokens={outOfTokens}
                    openMenuId={openMenuId}
                    setOpenMenuId={setOpenMenuId}
                    onShowRecharge={() => setRechargeDialogOpen(true)}
                    onOpenAlert={(id, label, existing) => setEditTarget({ credentialId: id, llmLabel: label, kind: "alert", existing })}
                    onOpenCutoff={(id, label, existing) => setEditTarget({ credentialId: id, llmLabel: label, kind: "cutoff", existing })}
                    onOpenReset={(id, label) => setEditTarget({ credentialId: id, llmLabel: label, kind: "reset" })}
                    onReactivate={reactivate}
                  />
                ))}
              </div>
            </>
          )}
        </>
      )}

      {rechargeDialogOpen && (
        <RechargeDialog
          tokensAvailable={tokensAvailable}
          onClose={() => setRechargeDialogOpen(false)}
          onGoToPlan={() => {
            setRechargeDialogOpen(false);
            navigate("/plan");
          }}
        />
      )}
    </div>
  );
}

function PlanUsageCard({ totalTokensUsed, totalAllowance, daysAvailable }) {
  const cycleLimit = 1_000_000;
  const cycleProgress = totalTokensUsed % 1_000_000;
  const percent = (cycleProgress / cycleLimit) * 100;
  const danger = percent >= 80 || daysAvailable <= 0;

  return (
    <Card>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <h3 style={{ fontSize: 15 }}>Plan usage (1M cycle)</h3>
          <p style={{ fontSize: 12, color: daysAvailable < 5 ? "var(--error)" : "var(--accent)", marginTop: 2 }}>
            {daysAvailable} days remaining
          </p>
        </div>
        <PercentBadge percent={percent} />
      </div>
      <div style={{ height: 10 }} />
      <UsageBar percent={percent} danger={danger} />
      <p style={{ marginTop: 8, fontSize: 12.5, color: "var(--slate)" }}>
        {totalTokensUsed.toLocaleString()} lifetime used / {totalAllowance.toLocaleString()} available. Bar resets
        every 1M tokens.
      </p>
    </Card>
  );
}

function PercentBadge({ percent }) {
  return <Badge tone={percent >= 80 ? "amber" : "primary"}>{percent.toFixed(1)}%</Badge>;
}

function ProviderMonitorCard({
  llm,
  cards,
  totalAllowance,
  outOfTokens,
  openMenuId,
  setOpenMenuId,
  onShowRecharge,
  onOpenAlert,
  onOpenCutoff,
  onOpenReset,
  onReactivate,
}) {
  return (
    <Card>
      <h3 style={{ fontSize: 15, color: "var(--accent-dark)", textTransform: "capitalize" }}>{llm}</h3>
      <div style={{ height: 10 }} />
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {cards.map((card, i) => (
          <ProviderUrlRow
            key={card.id}
            card={card}
            label={`Base URL #${i + 1}`}
            llmLabel={`${llm.toUpperCase()} Base URL #${i + 1}`}
            totalAllowance={totalAllowance}
            outOfTokens={outOfTokens}
            isMenuOpen={openMenuId === card.id}
            onMenuToggle={(open) => setOpenMenuId(open ? card.id : null)}
            onShowRecharge={onShowRecharge}
            onOpenAlert={onOpenAlert}
            onOpenCutoff={onOpenCutoff}
            onOpenReset={onOpenReset}
            onReactivate={onReactivate}
          />
        ))}
      </div>
    </Card>
  );
}

function ProviderUrlRow({
  card,
  label,
  llmLabel,
  totalAllowance,
  outOfTokens,
  isMenuOpen,
  onMenuToggle,
  onShowRecharge,
  onOpenAlert,
  onOpenCutoff,
  onOpenReset,
  onReactivate,
}) {
  const threshold = card.threshold;
  const isRed = threshold?.cutoff_tripped === true;
  const displayUsed = Math.max(0, (card.tokens_used ?? 0) - (card.baseline_tokens ?? 0));
  const percent = totalAllowance > 0 ? (displayUsed / totalAllowance) * 100 : 0;
  const isFull = percent >= 100;
  const hasAlertValue = (threshold?.alert_value ?? 0) > 0;
  const hasCutoff = threshold?.cutoff_active === true;

  // Native notification when cutoff trips -- only on the false -> true
  // transition, not on every render where it's still true (otherwise this
  // fires again on every Monitor remount/refresh while a cutoff stays
  // active, spamming the user with the same notification repeatedly).
  const wasRed = useRef(isRed);
  useEffect(() => {
    if (isRed && !wasRed.current) {
      notify("TokenGuard Alert", `${llmLabel} cutoff has been triggered.`);
    }
    wasRed.current = isRed;
  }, [isRed, llmLabel]);

  return (
    <div className="tg-provider-row">
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <StatusDot status={card.status} />
        <span style={{ flex: 1, fontSize: 13.5, fontWeight: 500 }}>{label}</span>
        {hasAlertValue && <Badge tone="primary">A</Badge>}
        {hasCutoff && <Badge tone="amber">C</Badge>}
        <span style={{ fontSize: 12.5, fontWeight: isFull ? 700 : 400, color: isFull ? "var(--error)" : "var(--slate)" }}>
          {percent.toFixed(1)}%
        </span>
      </div>

      <div style={{ height: 8 }} />
      <UsageBar percent={percent} danger={percent >= 80} />

      {isFull && (
        <p style={{ marginTop: 4, fontSize: 11.5, color: "var(--error)", fontWeight: 600 }}>
          Limit reached. Reset usage to continue tracking.
        </p>
      )}

      <div style={{ display: "flex", alignItems: "center", marginTop: 6, position: "relative" }}>
        <span style={{ flex: 1, fontSize: 11.5, color: "var(--slate)" }}>
          {displayUsed.toLocaleString()} used / {totalAllowance.toLocaleString()} profile tokens
        </span>
        <button className="tg-icon-btn" onClick={() => onMenuToggle(true)} aria-label="Row actions">
          <DotsIcon />
        </button>

        {isMenuOpen && (
          <>
            <div className="tg-menu-backdrop" onClick={() => onMenuToggle(false)} />
            <div className="tg-row-menu">
              <ThresholdChip
                active={hasAlertValue}
                activeLabel="Alert on"
                inactiveLabel="+ Alert"
                onClick={() => {
                  onMenuToggle(false);
                  if (outOfTokens) onShowRecharge();
                  else onOpenAlert(card.id, llmLabel, threshold);
                }}
              />
              <ThresholdChip
                active={hasCutoff}
                activeLabel="Cutoff on"
                inactiveLabel="+ Cutoff"
                onClick={() => {
                  onMenuToggle(false);
                  if (outOfTokens) onShowRecharge();
                  else onOpenCutoff(card.id, llmLabel, threshold);
                }}
              />
              <ThresholdChip
                active={isFull}
                activeLabel="Reset"
                inactiveLabel="Reset"
                onClick={() => {
                  onMenuToggle(false);
                  onOpenReset(card.id, llmLabel);
                }}
              />
            </div>
          </>
        )}
      </div>

      {isRed && (
        <div className="tg-cutoff-banner">
          <ErrorIcon />
          <span style={{ flex: 1 }}>Cutoff triggered</span>
          <button className="tg-btn tg-btn--text tg-btn--primary" style={{ fontSize: 11.5 }} onClick={() => onReactivate(card.id)}>
            Reactivate
          </button>
        </div>
      )}
    </div>
  );
}

function ThresholdChip({ active, activeLabel, inactiveLabel, onClick }) {
  return (
    <button className={`tg-chip ${active ? "tg-chip--active" : ""}`} onClick={onClick}>
      {active ? activeLabel : inactiveLabel}
    </button>
  );
}

function RechargeDialog({ tokensAvailable, onClose, onGoToPlan }) {
  const message =
    tokensAvailable <= 0
      ? "You have run out of tokens. Please recharge your balance to continue using monitoring features."
      : "Your access has expired. Please recharge your access days to continue using the gateway and dashboard.";
  return (
    <div className="tg-dialog-overlay" onClick={onClose}>
      <div className="tg-dialog" onClick={(e) => e.stopPropagation()}>
        <h2 style={{ fontSize: 18, marginBottom: 10 }}>Recharge needed</h2>
        <p style={{ fontSize: 13.5, color: "var(--slate)" }}>{message}</p>
        <div style={{ display: "flex", gap: 10, marginTop: 20, justifyContent: "flex-end" }}>
          <Button variant="text" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onGoToPlan}>Plan</Button>
        </div>
      </div>
    </div>
  );
}

function DotsIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <circle cx="5" cy="12" r="2" />
      <circle cx="12" cy="12" r="2" />
      <circle cx="19" cy="12" r="2" />
    </svg>
  );
}
function ErrorIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}
